import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { getPaymentStatus, getOrder } from '../lib/api';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, Ticket, ArrowRight } from 'lucide-react';

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [order, setOrder] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const res = await getPaymentStatus(sessionId);
      
      if (res.data.payment_status === 'paid') {
        setStatus('success');
        // Try to get order details
        // The order might take a moment to update
      } else if (res.data.status === 'expired') {
        setStatus('expired');
      } else {
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-20">
      <div className="glass-card p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 animate-spin mx-auto text-primary mb-4" />
            <h1 className="text-2xl font-bold mb-2">Procesando Pago</h1>
            <p className="text-muted-foreground">
              Estamos verificando tu pago, por favor espera...
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-secondary" />
            </div>
            <h1 className="text-2xl font-bold mb-2">¡Pago Exitoso!</h1>
            <p className="text-muted-foreground mb-6">
              Tu compra ha sido procesada correctamente. 
              Tus tickets están listos.
            </p>
            <div className="space-y-3">
              <Button
                className="w-full btn-primary"
                onClick={() => navigate('/tickets')}
                data-testid="view-tickets-button"
              >
                <Ticket className="w-4 h-4 mr-2" />
                Ver Mis Tickets
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/events')}
              >
                Explorar Más Eventos
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="w-20 h-20 rounded-full bg-destructive/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Sesión Expirada</h1>
            <p className="text-muted-foreground mb-6">
              La sesión de pago ha expirado. Por favor intenta de nuevo.
            </p>
            <Button
              className="w-full"
              onClick={() => navigate('/events')}
            >
              Volver a Eventos
            </Button>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-12 h-12 text-yellow-500" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Verificación en Proceso</h1>
            <p className="text-muted-foreground mb-6">
              La verificación está tomando más tiempo del esperado. 
              Revisa tu email para la confirmación.
            </p>
            <div className="space-y-3">
              <Button
                className="w-full"
                onClick={() => navigate('/tickets')}
              >
                Ver Mis Tickets
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setAttempts(0);
                  setStatus('loading');
                  pollPaymentStatus();
                }}
              >
                Reintentar Verificación
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
